<template>
  <div>
    <div ref="realgrid" :style="`height: ${height}px;  width: 100%`"></div>
  </div>
</template>

<script>
import RealGrid from 'realgrid/dist/main'
import Message from '@/utils/message'
import EventHandler from '@/utils/eventHandler'
import DispatchResize from '@/mixins/DispatchResize'
let defaultOptions = {
  gridView: {
    edit: {
      appendable: true,
      insertable: true,
      deletable: true
    }
  },
  dataProvider: {
    softDeleting: true
  }
}

export default {
  name: 'Realgrid',
  // 기본 사용 내역 리스트 업
  props: {
    // 조회 전용 API
    retrieveApi: Function,
    // 저장 (추가/수정/삭제) 대응하는 전용 API
    saveApi: Function,
    // Grid를 정의하기 위한 옵션
    // Realgrid에 setOptions()에 사용되는 형식으로 구성하여 전달해야 한다.
    // grid와 this.dataProvider 각각 2개 영역으로 나눠져야 한다.
    options: {
      type: Object,
      required: false,
      default() {
        return defaultOptions
      }
    },
    // 조회 시, 사용되는 옵션 값
    retrieveData: Object,
    // Realgrid Data Provider에 사용되는 기본 열 정의
    fields: Array,
    // Realgrid Grid View에 사용되는 기본 열 정의
    columns: Array,
    // Realgrid Grid의 Height 지정, 부모에서 값을 지정하지 않으면, 500으로 자동 셋팅 된다.
    height: {
      type: Number,
      required: false,
      default: 500
    }
  },
  mixins: [DispatchResize()],
  data() {
    return {
      gridView: null,
      dataProvider: null,
      // 조회 시, 사용했던 조회 조건 저장 부분
      // Save 사용 간 사용하게 된다.
      retrieveOriginData: {},
      inited: false,
      contextMenuArray: [
        { label: 'Column Filter 켜키', type: 'normal', tag: 'filter_on' },
        { label: 'Column Filter 끄기', type: 'normal', tag: 'filter_off' },
        { label: '-' }, // 메뉴 구분선 추가
        { label: 'Excel Export', type: 'normal', tag: 'export' }
      ]
    }
  },
  mounted: function() {
    this.initGrid()
  },
  methods: {
    // retrieveApi를 통해 자동으로 그리드의 내용을 조회하여 반영한다.
    resetSize() {
      this.gridView.resetSize()
    },
    retrieve() {
      if (typeof this.retrieveApi !== 'function') return
      let reqObj = {}

      if (this.retrieveData) reqObj = this.retrieveData

      // 조회 시, 사용했던 조건 데이터들은 retrieveOriginData에 저장되어, 차후 Save 시 사용된다.
      this.retrieveOriginData = reqObj

      // 데이터를 가져오는 retrieveApi를 API 실행
      return new Promise((resolve, reject) => {
        this.retrieveApi(reqObj)
          .then(response => {
            // RealGrid에 조회한 데이터를 넣는다.
            // callback object 분기처리
            if (response.dataList !== undefined) {
              this.dataProvider.fillJsonData(response.dataList)
            } else {
              this.dataProvider.fillJsonData(response)
            }
            this.inited = true
            resolve()
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    // 해당 그리드에 새로운 Row를 추가한다.
    addRow() {
      // 조회 되지 않는 그리드에서는 해당 기능을 사용할 수 없다.
      if (!this.inited) return
      // 수정 도중이면 현재 상황을 정리한다.
      // commit은 수정 줄이 수정 상태 그대로, cancel은 수정 이전 상태로 변경
      this.gridView.commit()

      var targetRowIndex = this.gridView.getCurrent()

      if (targetRowIndex.itemIndex < 0) {
        this.gridView.beginAppendRow()
      } else {
        this.gridView.beginInsertRow(targetRowIndex.itemIndex, true)
      }
    },
    // 선택된 Current Row에 삭제 표시를 한다.
    // 차후, 저장 시 삭제 대상이 된다.
    deleteRow() {
      // 조회 되지 않는 그리드에서는 해당 기능을 사용할 수 없다.
      if (!this.inited) return
      // 옵션이 true일 경우 묻지않고 삭제 대상이 된다.
      this.gridView.deleteSelection(true)
    },
    // saveApi에 정의한 API를 호출한다.
    // 조회 때 저장한 조건 데이터를 가져와 사용한다.
    save() {
      // 조회 되지 않는 그리드에서는 해당 기능을 사용할 수 없다.
      if (!this.inited) return
      // commit은 수정 줄이 수정 상태 그대로, cancel은 수정 이전 상태로 변경
      this.gridView.commit()

      // saveApi에 API 매칭되어야 기능을 사용할 수 있다.
      if (typeof this.saveApi !== 'function') return
      var gridSaveData = {
        updated: [],
        created: [],
        deleted: []
      }
      // 각 상태 별 값을 가져온다.
      // RealGrid에서 getStateRows를 통해 각 상태별 dataprovier 내 index 정보를 가져온다.
      var updatedRows = this.dataProvider.getStateRows('updated')
      var createdRows = this.dataProvider.getStateRows('created')
      var deletedRows = this.dataProvider.getStateRows('deleted')

      // index 정보를 기준으로 각 실제 값을 가져온다.
      updatedRows.forEach((row, idx) => {
        gridSaveData.updated.push(this.dataProvider.getJsonRow(row))
      })
      createdRows.forEach((row, idx) => {
        gridSaveData.created.push(this.dataProvider.getJsonRow(row))
      })
      deletedRows.forEach((row, idx) => {
        gridSaveData.deleted.push(this.dataProvider.getJsonRow(row))
      })

      gridSaveData.retrieveData = this.retrieveOriginData

      // 해당 데이터는 아래와 같이 추가된다.
      // 이 값들은 Payload 곳에 저장되어 서버에 전송 되는 것을 권장한다.
      /*
      {
        retrieveData: {}, // 조회 시, 사용했던 조건 데이터, 서버에도 전송된다.
        updated: [], // 수정 했던 Row 데이터들이 들어간다.
        created: [], // 추가 했던 Row 데이터들이 들어간다.
        deleted: []  // 삭제 했던 Row 데이터들이 들어간다.
      }
      */

      // 전송
      return new Promise((resolve, reject) => {
        // 조회 시, 조건 파라미터들은 this.retrieveOriginData 저장되어 있으며,
        // 이 값을 저장 시에도 조건으로 사용한다.
        this.saveApi(gridSaveData)
          .then(response => {
            // 저장 완료되면, 그리드를 재 조회 한다.
            this.retrieve()
            resolve()
          })
          .catch(error => {
            reject(error)
          })
      })
    },
    realgridLoadHandler() {
      return new Promise((r, e) => {
        EventHandler.once(() => {
          r()
        }, 'loaded-realgridjs')
      })
    },
    initGrid() {
      // await this.realgridLoadHandler()
      if (!this.fields) {
        console.warn(
          '[UI Framework] rlgrid Component 내 fields를 정의하지 않았습니다.'
        )
        return
      }
      if (!this.columns) {
        console.warn(
          '[UI Framework] Realgrid Component 내 columns를 정의하지 않았습니다.'
        )
        return
      }
      // eslint-disable-next-line
      this.dataProvider = new RealGrid.LocalDataProvider()
      // eslint-disable-next-line
      this.gridView = new RealGrid.GridView(this.$refs.realgrid)
      this.gridView.setDataSource(this.dataProvider)

      // this.gridView에 옵션을 적용한다.
      if (this.options && this.options.gridView) {
        this.gridView.setOptions(this.options.gridView)
      } else {
        // options 값이 설정되지 않으면 아래의 값으로 설정된다.
        this.gridView.setOptions(defaultOptions.gridView)
      }
      // 필터 옵션 설정
      this.gridView.setFilteringOptions({
        // realgrid2.0에서 사라짐
        // handleBorderColor: '#000000', // 필터핸들의 경계선 색상
        // handleColor: '#000000', // 핸들의 색상
        // hoveredHandleColor: '#000000', // 마우스가 호버되고, 필터링되었을때의 색상을 지정
        // hoveredHandleNoneColor: '#7c8991', // 마우스가 호버되고, 필터링 되지 않았을때의 색상
        selector: {
          // realgrid2.0에서 사라짐
          // maxWidth: 200,
          // maxHeight: 400,
          // showSearchInput: true, // 검색 영역 표시 여부
          showButtons: true, // 버튼 표시 여부
          acceptText: '확인', // 필터 적용 버튼 text
          cancelText: '취소' //  취소 버튼 text
          // realgrid2.0에서 사라짐
          // useCssStyle: true
        }
      })

      this.gridView.setContextMenu(this.contextMenuArray) // 컨텍스트 메뉴 설정

      // 컨텍스트 메뉴가 실행될 때 호출되는 함수, false 를 리턴하면 컨텍스트 메뉴를 실행하지 않는다.
      this.gridView.onContextMenuPopup = (cgrid, x, y, elementName) => {
        // 조회 전이거나, 조회후에 그리드 데이터가 없으면 컨텍스트 메뉴 실행하지 않음
        if (!this.inited || this.dataProvider.getRowCount() <= 0) return false
        // 헤더, 푸터, 그룹핑 패널 영역에서는 컨텍스트 메뉴 실행하지 않음
        if (elementName.cellType !== 'data') return false
      }

      this.gridView.onContextMenuItemClicked = (cgrid, menuItem, index) => {
        // 선택된 contextmenu 항목에 따라 처리
        switch (menuItem.tag) {
          case 'export': // 엑셀 다운로드
            this.gridView.exportGrid({
              type: 'excel',
              target: 'local',
              fileName: 'gridExportSample.xlsx', // 저장될 파일 name
              progressMessage: '엑셀 다운로드중입니다.', // 엑셀 다운로드 하는 동안 progressMessage 표시
              // showProgress: true, // 엑셀 다운로드 하는 동안 progressBar 표시
              indicator: 'visible', // indidator 영역 표시
              header: 'visible', // 헤더 표시
              footer: 'hidden', // footer 표시하지 않음
              allColumns: true, // visible : false 인 행도 표시할 것인지 여부
              done: () => {
                // 내보내기 완료 후 실행되는 함수
                Message.success('엑셀 다운로드가 완료되었습니다.')
              }
            })
            break
          case 'filter_on': // Column Filter 켜키
            var distinctValues = this.dataProvider.getDistinctValues(
              index.column
            ) // 지정된 컬럼의 데이터들을 중복을 제거하고 가져온다.
            var filters = []
            for (var value of distinctValues) {
              // 중복을 제거한 데이터를 넣어준다.
              // name: 사용자에게 보여질 값
              // criteria: 해당 필터 선택시 필터 조건
              filters.push({ name: value, criteria: "value = '" + value + "'" })
            }
            this.gridView.setColumnFilters(index.column, filters) // 선택된 컬럼에 필터 셋팅
            break
          case 'filter_off': // Column Filter 끄기
            this.gridView.clearColumnFilters(index.column) // 해당 컬럼의 필터 설정을 해제한다
            break
        }
      }
      // dataProvicder에 옵션을 적용한다.
      if (this.options && this.options.dataProvider) {
        // this.dataProvider에 사전에 정의한 옵션을 적용한다.
        this.dataProvider.setOptions(this.options.dataProvider)
      } else {
        // this.dataProvider에 기본 옵션을 적용한다.
        this.dataProvider.setOptions(defaultOptions.dataProvider)
      }

      this.dataProvider.setFields(this.fields)
      this.gridView.setColumns(this.columns)
      // this.$nextTick(() => {
      //   // 내부 그리드의 width가 시점 문제로 조정 되지 않는 이슈는 글로벌 이벤트로 해결
      //   this.dispatchResize()
      // })
      // realgrid가 적용 완료됨을 전달
      this.$emit('loaded')
    },
    getGridView() {
      return this.gridView
    },
    getDataProvider() {
      return this.dataProvider
    },
    excelDown(fileName) {
      if (fileName) {
        fileName = fileName.trim()
      }
      this.gridView.commit()
      this.gridView.exportGrid({
        type: 'excel',
        target: 'local',
        fileName: fileName ? fileName + '.xlsx' : 'gridExport.xlsx', // 저장될 파일 name
        progressMessage: '엑셀 다운로드중입니다.', // 엑셀 다운로드 하는 동안 progressMessage 표시
        showProgress: true, // 엑셀 다운로드 하는 동안 progressBar 표시
        indicator: 'visible', // indidator 영역 표시
        header: 'visible', // 헤더 표시
        footer: 'hidden', // footer 표시하지 않음
        allColumns: true, // visible : false 인 행도 표시할 것인지 여부
        done: () => {
          // 내보내기 완료 후 실행되는 함수
          Message.success('엑셀 다운로드가 완료되었습니다.')
        }
      })
    }
  },
  beforeDestroy: function() {
    /**
     * RealGrid를 사용할 경우, lifecycle 내 beforeDestroy 항목에서,
     * RealGrid가 사용한 리소스를 해제해야 하는 작업이 필요합니다.
     */

    // 데이터 초기화
    this.dataProvider.clearRows()

    // grid, provider 초기화
    this.gridView.destroy()
    this.dataProvider.destroy()

    // LocalDataProvider와 GridView 객체 초기화
    this.gridView = null
    this.dataProvider = null
  }
}
</script>

<style>
</style>
